import { memo } from 'react'
import { Img } from 'src/components/elements'
import round from 'src/helpers/round'
import useMountAndOpenModal from 'src/hooks/useMountAndOpenModal'
import { ChannelModel, InitDataModel } from 'src/models/InitDataModel'
import ModalShowMoreItems, { IModalShowMoreItems } from './ModalShowMoreItems'
import addComma from 'src/helpers/addComma'

interface Props {
  initialData: InitDataModel
  selectedChannel: ChannelModel | null
}

const PaymentSummary: React.FC<Props> = ({ initialData, selectedChannel }) => {
  const MAX_ITEM_TO_SHOW = 3
  const [modal, createModal] = useMountAndOpenModal()
  const getServiceFee = (): number => {
    const service_fee_details = selectedChannel?.service_fee_details ?? null
    const fee_details = selectedChannel?.fee_details ?? null
    const sub_amount = initialData?.subtotal_amount ?? 0

    if (fee_details && fee_details.fee_type) {
      switch (fee_details.fee_type) {
        case 'fixed':
          return fee_details.fixed_fee ?? 0
        case 'percentage':
          return ((fee_details.percentage_fee ?? 0) / 100) * sub_amount
        case 'percentage-plus-fixed':
          return (fee_details.fixed_fee ?? 0) + (((fee_details.percentage_fee ?? 0) / 100) * sub_amount)
        case 'whichever-is-higher':
          return Math.max(
            fee_details.fixed_fee ?? 0,
            ((fee_details.percentage_fee ?? 0) / 100) * sub_amount
          )
        default:
          throw new Error(`Unknown fee type: ${fee_details.fee_type}`)
      }
    }

    if (service_fee_details) {
      if (service_fee_details.is_fee_percentage === true) {
        return (service_fee_details.service_fee ?? 0) * 0.01 * sub_amount
      }
      return service_fee_details.service_fee ?? 0
    }

    return 0
  }

  const getServiceFeeHint = (): string => {
    const service_fee_details = selectedChannel?.service_fee_details
    const fee_details = selectedChannel?.fee_details
    const sub_amount = initialData?.subtotal_amount ?? 0

    if (fee_details && typeof fee_details.fee_type === 'string') {
      switch (fee_details.fee_type) {
        case 'fixed':
          return ''
        case 'percentage':
          return fee_details.percentage_fee !== undefined
            ? `${fee_details.percentage_fee.toString()}%`
            : ''
        case 'percentage-plus-fixed':
          return fee_details.percentage_fee !== undefined && fee_details.fixed_fee !== undefined
            ? `${fee_details.percentage_fee.toString()}% + ₱${fee_details.fixed_fee.toString()}`
            : ''
        case 'whichever-is-higher':
          if (
            fee_details.percentage_fee !== undefined &&
            fee_details.fixed_fee !== undefined
          ) {
            const percentValue = (sub_amount * fee_details.percentage_fee) / 100
            const fixedValue = fee_details.fixed_fee
            if (percentValue > fixedValue) {
              return `${fee_details.percentage_fee.toString()}%`
            } else {
              return ''
            }
          }
          return ''
        default:
          return ''
      }
    }

    if (service_fee_details) {
      const serviceFee = service_fee_details.service_fee ?? 0
      if (service_fee_details.is_fee_percentage === true) {
        return `${serviceFee.toString()}%`
      }
      return ''
    }

    return ''
  }

  const serviceFee = getServiceFee()

  const serviceFeeHint = getServiceFeeHint()

  const openModalShowMoreItems = (props: IModalShowMoreItems) => {
    createModal(ModalShowMoreItems, props)
  }

  return (
    <>
      <div className="flex flex-col items-center space-y-2 ">
        <Img
          height="60"
          width="60"
          draggable={false}
          className="object-contain shadow-[0_0_4px_0_rgba(109,91,208,0.15)] rounded-md p-2 h-[3.75rem] w-[3.75rem]"
          src={
            initialData?.biller.organization_logo
              ? initialData?.biller?.organization_logo
              : '/images/artwork/broken-image.svg'
          }
          alt={
            initialData?.biller.organization
              ? initialData?.biller?.organization
              : 'techpay'
          }
        />
        <div className="font-semibold">{initialData?.biller?.organization}</div>
        <div className="font-semibold text-[12px] uppercase bg-[#FFF2C9] border rounded px-6 py-2">
          INITIAL
        </div>
      </div>

      <div className=" text-base flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div>Paying For</div>
          <div>
            ₱{` `}
            {addComma(round(initialData?.subtotal_amount, 2))}
          </div>
        </div>

        {initialData.biller.settings.disable_showing_of_items_on_payflow ===
          false &&
          initialData.items.length > 0 && (
            <div
              onClick={() =>
                openModalShowMoreItems({
                  items: initialData.items,
                  transactionFee: serviceFee,
                  totalAmount: serviceFee + initialData?.subtotal_amount,
                })
              }
              className="w-full py-2 sm:py-4 border-b border-t text-xs cursor-pointer group"
            >
              <div className="grid grid-cols-8 gap-2 w-full">
                <div className="font-semibold text-left col-span-4">Item</div>
                <div className="font-semibold text-center col-span-2">Qty</div>
                <div className="font-semibold text-right col-span-2">
                  Amount
                </div>
                {initialData.items
                  .slice(0, MAX_ITEM_TO_SHOW)
                  .map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-col w-full col-span-8"
                    >
                      <div className=" col-span-3 grid grid-cols-8 gap-2 w-full items-center">
                        <div className=" text-left font-medium col-span-4">
                          <p className="truncate" title={item.name}>
                            {item.name}
                          </p>
                          <p
                            title={item.description ?? ''}
                            className="truncate opacity-50"
                          >
                            {item.description ?? <em>(No description)</em>}
                          </p>
                        </div>
                        <div className="text-center font-semibold col-span-2">
                          {item.quantity}
                        </div>
                        <div className="text-right col-span-2">
                          ₱{` `}
                          {addComma(round(item.amount, 2))}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              {MAX_ITEM_TO_SHOW < initialData.items.length && (
                <div className="flex items-center justify-between mt-4 font-semibold">
                  <p>
                    and {initialData.items.length - MAX_ITEM_TO_SHOW} more{' '}
                    {initialData.items.length - MAX_ITEM_TO_SHOW <= 1
                      ? 'item'
                      : 'items'}
                    ...
                  </p>

                  <div className="text-primary group-hover:underline">
                    Show All
                  </div>
                </div>
              )}
            </div>
          )}

        <div className="flex items-center justify-between">
          <div>
            <div>Transaction Fee</div>
            {serviceFeeHint ? (
              <div>
                <small>({serviceFeeHint})</small>
              </div>
            ) : ''}
          </div>
          <div>
            ₱{` `}
            {addComma(round(serviceFee, 2))}
          </div>
        </div>

        <div className="flex items-center justify-between text-primary font-bold">
          <div>Total Amount</div>
          <div className="font-semibold">
            ₱{` `}
            {addComma(round(serviceFee + initialData?.subtotal_amount, 2))}
          </div>
        </div>
      </div>
      {modal}
    </>
  )
}

export default memo(PaymentSummary)
